import React from 'react';
import { Type, Image, Square, MousePointer, Minus, Database } from 'lucide-react';
import { BannerElement, DataFeed } from '../types';

interface ElementLibraryProps {
  onAddElement: (type: BannerElement['type']) => void;
  dataFeed: DataFeed | null;
  onSetDataFeed: (data: DataFeed) => void;
}

export const ElementLibrary: React.FC<ElementLibraryProps> = ({
  onAddElement,
  dataFeed,
  onSetDataFeed
}) => {
  const handleDataFeedUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target?.result as string);
          
          // Handle both array and object formats
          let processedData: DataFeed;
          if (Array.isArray(data)) {
            // If it's an array, take the first item
            processedData = data[0] || {};
          } else {
            // If it's already an object, use it directly
            processedData = data;
          }
          
          onSetDataFeed(processedData);
        } catch (error) {
          alert('Invalid JSON file');
        }
      };
      reader.readAsText(file);
    }
  };

  const getDataFeedKeys = () => {
    if (!dataFeed) return [];
    return Object.keys(dataFeed).filter(key => key !== 'length');
  };

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto">
      <div className="p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Elements</h2>
        
        <div className="space-y-2 mb-6">
          <button
            onClick={() => onAddElement('text')}
            className="w-full flex items-center space-x-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
          >
            <Type size={20} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Text</span>
          </button>
          
          <button
            onClick={() => onAddElement('image')}
            className="w-full flex items-center space-x-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
          >
            <Image size={20} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Image</span>
          </button>
          
          <button
            onClick={() => onAddElement('shape')}
            className="w-full flex items-center space-x-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
          >
            <Square size={20} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Shape</span>
          </button>
          
          <button
            onClick={() => onAddElement('button')}
            className="w-full flex items-center space-x-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
          >
            <MousePointer size={20} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Button</span>
          </button>
          
          <button
            onClick={() => onAddElement('divider')}
            className="w-full flex items-center space-x-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
          >
            <Minus size={20} className="text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Divider</span>
          </button>
        </div>

        <h3 className="text-sm font-semibold text-gray-900 mb-3">Data Feed</h3>
        
        <div className="space-y-3">
          <input
            type="file"
            accept=".json"
            onChange={handleDataFeedUpload}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          
          {dataFeed && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <Database size={16} className="text-green-600" />
                <span className="text-sm font-medium text-green-800">Data Connected</span>
              </div>
              <div className="text-xs text-green-700 space-y-1">
                {getDataFeedKeys().slice(0, 3).map(key => (
                  <div key={key}>
                    {key.charAt(0).toUpperCase() + key.slice(1)}: {String(dataFeed[key]).substring(0, 20)}
                    {String(dataFeed[key]).length > 20 ? '...' : ''}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {dataFeed && (
          <div className="mt-4">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Available Fields:</h4>
            <div className="text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
              {getDataFeedKeys().map(key => (
                <div key={key} className="font-mono bg-gray-50 px-2 py-1 rounded text-xs">
                  {`{{${key}}}`}
                </div>
              ))}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Drag these fields into text/image elements or use the dropdown in Properties panel
            </div>
          </div>
        )}
      </div>
    </div>
  );
};